import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Button, FormField, PasswordInput } from "@/design-system/components/ui";

// Staff counterpart of the portal set-password page: landing for the
// admin "Send welcome email" link. Consumes the one-time token from
// ?token=..., accepts a new password (twice), POSTs to
// /api/auth/set-password/confirm, then directs the user to sign-in.
// The token is validated up front (non-consuming) so a stale / used link
// shows a clear message instead of letting the user fill the form only
// to be rejected on submit.

const MIN_LENGTH = 12;

type TokenState = "checking" | "valid" | "invalid";

export function SetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [tokenState, setTokenState] = useState<TokenState>("checking");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!token) {
        setTokenState("invalid");
        return;
      }
      try {
        const status = await authApi.validateSetPasswordToken(token);
        if (!cancelled) setTokenState(status.valid ? "valid" : "invalid");
      } catch {
        if (!cancelled) setTokenState("invalid");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.confirmSetPassword(token, pw);
      setDone(true);
    } catch (err) {
      const message =
        err instanceof ApiError && err.errors.length
          ? err.errors.join(" ")
          : err instanceof Error
            ? err.message
            : "Couldn't set your password.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black-pure text-white-pure flex items-center justify-center p-space-5">
      <div
        className="w-[440px] max-w-full bg-onyx border border-border-dark border-t-[2px] border-t-gold rounded-lg"
        style={{ padding: 40 }}
      >
        <img
          src="/assets/logo/phoenix-logo-horizontal-dark.jpeg"
          alt="Phoenix Capital Solutions"
          className="mx-auto block mb-space-3"
          style={{ height: 80 }}
        />

        {tokenState === "checking" && (
          <p
            className="text-center text-caption"
            style={{ color: "#9A9A9A" }}
            data-testid="set-password-checking"
          >
            Checking your link…
          </p>
        )}

        {tokenState === "invalid" && (
          <div data-testid="set-password-invalid">
            <p className="text-center text-small mb-space-3">
              This link is no longer valid — it may have expired or already
              been used. Ask your administrator to send a new welcome email.
            </p>
            <p className="text-center text-caption">
              <Link to="/login" className="text-gold hover:underline">
                Go to sign-in
              </Link>
            </p>
          </div>
        )}

        {tokenState === "valid" && done && (
          <div data-testid="set-password-done">
            <p className="text-center text-small mb-space-3">
              Your password is saved. Sign in with your email and your new
              password.
            </p>
            <p className="text-center text-caption">
              <Link to="/login" className="text-gold hover:underline">
                Go to sign-in
              </Link>
            </p>
          </div>
        )}

        {tokenState === "valid" && !done && (
          <>
            <p className="text-center text-caption mb-space-5" style={{ color: "#9A9A9A" }}>
              Choose a password to finish setting up your account.
            </p>
            <form onSubmit={onSubmit} className="flex flex-col gap-space-3" noValidate>
              <FormField
                label={`New password (at least ${MIN_LENGTH} characters)`}
                required
                htmlFor="set-password-new"
              >
                <PasswordInput
                  id="set-password-new"
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  className="text-white-pure placeholder:text-gray-mid border-border-dark focus:border-gold"
                  style={{ background: "#0F0F0F" }}
                />
              </FormField>
              <FormField label="Confirm new password" required htmlFor="set-password-confirm">
                <PasswordInput
                  id="set-password-confirm"
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  required
                  className="text-white-pure placeholder:text-gray-mid border-border-dark focus:border-gold"
                  style={{ background: "#0F0F0F" }}
                />
              </FormField>

              {error && (
                <p className="text-label text-status-error" role="alert">
                  {error}
                </p>
              )}

              <Button type="submit" variant="primary" size="lg" loading={submitting} className="w-full">
                Save password
              </Button>
            </form>
          </>
        )}

        <p className="text-center mt-space-4" style={{ fontSize: 12, color: "#7A7A7A" }}>
          Proprietary &amp; Confidential · Phoenix Capital Solutions
        </p>
      </div>
    </div>
  );
}
